import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  resetFilterData,
  setGridCurrentPage,
  setFilterDataField,
  setATECases,
} from "src/redux/slices/ateCasesSlice";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { showHistoricalDataChangeSource } from "src/resources/enum";
import {
  getFieldName,
  convertIsoStringToDate,
  isCivilDefenseUser,
} from "src/utils/helpers";
import moment from "moment";
import { toast } from "react-toastify";
import useOnPageLeave from "src/hooks/useOnPageLeave";
import ToggleFilter from "src/components/ToggleFilter/ToggleFilter";
import useResize from "src/hooks/useResize";
import { useRef, useState, useCallback } from "react";
import FilterActionButtons from "src/components/FilterActionButtons/FilterActionButtons";

import {
  isSelectedFilterStatusIsCompleted,
  onHideCalendar,
  onShowCalendar,
} from "src/utils/common";
import {
  getServiceTerritories,
  setServiceTerritories,
} from "src/redux/slices/lookupSlice";
import { setChangeHistoricalDataSource } from "src/redux/slices/sharedSlice";
import { MultiSelect } from "primereact/multiselect";

const ATECasesFilter = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "registration", "dashboard"]);

  const bmcList = useSelector((state: any) => state.civilDefense.bmcList);
  const { isMobile } = useResize();
  const [isFilterShown, setIsFilterShown] = useState(!isMobile);
  const pathName = window.location.pathname;
  const [isBreached, setIsBreached] = useState<any>(
    pathName.includes("breached") ? true : false
  );

  const creationDateFromRef = useRef<Calendar | null>(null);
  const creationDateToRef = useRef<Calendar | null>(null);

  const assignedBuildingData = useSelector(
    (state: any) => state.lookup.assignedBuildings
  );
  const {
    propertyId,
    sLABreached,
    systemServiceId,
    territoryId,
    company,
    cityId,
    status,
    startDate,
    endDate,
    managerSupervisorName,
  } = useSelector((state: any) => state.ateCases.filterData);

  const sLAOptions = [
    { label: t("exceeded"), value: true },
    { label: t("onTrack"), value: false },
  ];

  const systemNameOptions = useSelector((state: any) => state.lookup.systems);
  const serviceTerritories = useSelector(
    (state: any) => state.lookup.serviceTerritories
  );
  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);
  const statuses = useSelector((state: any) => state.lookup.statusOptions);
  const language = useSelector((state: any) => state.lookup.language);

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
  };

  const handleFilter = () => {
    goToGridFirstPage();
  };

  const handleFormFieldChange = (e: any) => {
    let name, value;
    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }
    dispatch(setFilterDataField({ field: name, value }));
    if (name === "startDate" && endDate) {
      const startDateValue = moment(value);
      const endDateValue = moment(endDate);
      if (startDateValue.isAfter(endDateValue)) {
        toast.error(t("startEndDateValidation"));
        dispatch(setFilterDataField({ field: "endDate", value }));
      }
    }

    if (name === "status") {
      dispatch(
        setChangeHistoricalDataSource(showHistoricalDataChangeSource.ATECase)
      );
      dispatch(
        setFilterDataField({
          field: "showHistoricalData",
          value: isSelectedFilterStatusIsCompleted(statuses, value),
        })
      );
    }
  };

  const handleCityChange = (e: any) => {
    handleFormFieldChange(e);
    dispatch(setFilterDataField({ field: "territoryId", value: "" }));
    dispatch(setServiceTerritories([]));
    dispatch(getServiceTerritories(false, e.value) as any);
  };

  const handleClearFilterForm = (): void => {
    dispatch(resetFilterData());
    goToGridFirstPage();
  };

  const goToGridFirstPage = useCallback(() => {
    dispatch(setGridCurrentPage(0));
  }, []);

  const onPageLeave = useCallback(() => {
    dispatch(resetFilterData());
    dispatch(setATECases({ data: [], totalRecords: 0 }));
  }, []);
  useOnPageLeave(onPageLeave);

  const toggleFilter = () => {
    setIsFilterShown(!isFilterShown);
  };

  return (
    <>
      <ToggleFilter onClick={toggleFilter} />
      <div
        className={`px-2 pb-2 pt-3 maintenance-requests-filter ${
          !isMobile || isFilterShown ? "d-flex" : "d-none"
        } ${isMobile ? "my-3" : ""} `}
      >
        <p className="d-flex m-0 me-2 mb-2 ms-2 fit-content filter-by-label">
          {t("filterBy")}
        </p>
        <form onSubmit={handleFormSubmit}>
          <div className="filter-row mb-2">
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              emptyMessage={t("noResultsFound")}
              options={assignedBuildingData}
              placeholder={t("filterByBuilding")}
              className="sla-dropdown"
              optionLabel="buildingName"
              optionValue="propertyId"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={propertyId}
              name="propertyId"
            />
            {!isBreached && (
              <Dropdown
                filter
                resetFilterOnHide
                emptyFilterMessage={t("noResultsFound")}
                emptyMessage={t("noResultsFound")}
                options={sLAOptions}
                placeholder={t("sLABreached")}
                className="sla-dropdown"
                optionLabel="label"
                optionValue="value"
                panelClassName="status-dropdown-panel"
                onChange={handleFormFieldChange}
                value={sLABreached}
                name="sLABreached"
              />
            )}
            <Dropdown
              filter
              resetFilterOnHide
              emptyMessage={t("noResultsFound")}
              emptyFilterMessage={t("noResultsFound")}
              options={systemNameOptions}
              placeholder={t("systemName")}
              className="system-name-dropdown"
              optionLabel={getFieldName(language)}
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              optionValue="systemServiceId"
              value={systemServiceId}
              name="systemServiceId"
            />

            {isCivilDefenseUser() && (
              <Dropdown
                filter
                resetFilterOnHide
                emptyMessage={t("noResultsFound")}
                emptyFilterMessage={t("noResultsFound")}
                id="company"
                name="company"
                value={company}
                optionValue={"id"}
                optionLabel={"name"}
                onChange={handleFormFieldChange}
                options={bmcList}
                placeholder={t("company")}
                panelClassName="status-dropdown-panel"
              />
            )}

            <Dropdown
              filter
              resetFilterOnHide
              emptyMessage={t("noResultsFound")}
              emptyFilterMessage={t("noResultsFound")}
              options={citiesOptions}
              placeholder={t("City")}
              className="city-dropdown"
              optionLabel={getFieldName(language)}
              optionValue="cityId"
              panelClassName="status-dropdown-panel"
              onChange={handleCityChange}
              value={cityId}
              name="cityId"
            />

            {isCivilDefenseUser() ? (
              <MultiSelect
                filter
                resetFilterOnHide
                emptyMessage={t("noResultsFound")}
                emptyFilterMessage={t("noResultsFound")}
                options={cityId ? serviceTerritories : []}
                placeholder={t("territory") + " - " + t("selectCityFirstShort")}
                className="status-dropdown"
                optionValue={"territoryId"}
                optionLabel={getFieldName(language)}
                panelClassName="status-dropdown-panel"
                onChange={handleFormFieldChange}
                value={territoryId}
                name="territoryId"
              />
            ) : (
              <Dropdown
                filter
                resetFilterOnHide
                emptyFilterMessage={t("noResultsFound")}
                emptyMessage={t("noResultsFound")}
                options={cityId ? serviceTerritories : []}
                placeholder={t("territory") + " - " + t("selectCityFirstShort")}
                className="service-territory-dropdown"
                optionValue={"territoryId"}
                optionLabel={getFieldName(language)}
                panelClassName="status-dropdown-panel"
                onChange={handleFormFieldChange}
                value={territoryId}
                name="territoryId"
              />
            )}

            <Dropdown
              filter
              resetFilterOnHide
              emptyMessage={t("noResultsFound")}
              emptyFilterMessage={t("noResultsFound")}
              options={statuses}
              placeholder={t("status")}
              className="status-dropdown"
              optionLabel={getFieldName(language)}
              optionValue="id"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={status}
              name="status"
            />
            <Calendar
              value={startDate}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("creationDateFrom")}
              name="startDate"
              dateFormat="dd/mm/yy"
              showIcon
              maxDate={new Date()}
              ref={creationDateFromRef}
              onHide={() => onHideCalendar(creationDateFromRef)}
              onShow={() => onShowCalendar(creationDateFromRef)}
              key={`startDate-${startDate?.toString()}`}
            />

            <Calendar
              value={endDate}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("creationDateTo")}
              viewDate={endDate ? endDate : new Date()}
              name="endDate"
              dateFormat="dd/mm/yy"
              minDate={
                !startDate
                  ? new Date(-8640000000000000)
                  : convertIsoStringToDate(startDate)
              }
              ref={creationDateToRef}
              onHide={() => onHideCalendar(creationDateToRef)}
              onShow={() => onShowCalendar(creationDateToRef)}
              key={`endDate-${endDate?.toString()}`}
            />
            {!isCivilDefenseUser() && (
              <InputText
                type="text"
                placeholder={t("managerSupervisorName")}
                onChange={handleFormFieldChange}
                value={managerSupervisorName}
                name="managerSupervisorName"
                autoComplete="off"
              />
            )}
            <FilterActionButtons
              handleFilter={handleFilter}
              handleClear={handleClearFilterForm}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default ATECasesFilter;
